<!-- eslint-disable no-undef -->
<!-- eslint-disable vue/no-setup-props-destructure -->
<script setup>

    // Imports
    import { onMounted, ref, watch, defineExpose } from 'vue';
    import { TrashIcon } from '@heroicons/vue/24/solid';

    import { getPastTimestamp, localToUTC, utcToLocal } from '@/tools/TimeFunctions';
    import ChooseColumns from '@/components/modals/ChooseColumns.vue';
    import LoadingAnimation3 from '@/components/loadingIcons/LoadingAnimation3.vue';
    import DatePicker from '@/components/dropdowns/DatePicker.vue';
    import GenericTable from '@/components/tables/GenericTable2.vue';
    import Events from '@/api/event';
    import CSVReportGenerator from '@/tools/EventsReportGenerator';
    import CreateReport from '@/components/modals/CreateReport.vue';

    // Definitions
    const emit = defineEmits(['update', 'delete']);

    const props = defineProps({
        dataID: String,
        template: Object,
        eventJustCreated: {
            type: Boolean,
            default: false
        },
        canEdit: {
            type: Boolean,
            default: true
        },
        canDelete: {
            type: Boolean,
            default: true
        }
    });

    // References
    const rawData = ref();
    const data = ref([]);
    const opperations = ref(null);
    const startDate = ref(null);
    const dateValue = ref([getPastTimestamp(24, 'local'), getPastTimestamp(0, 'local')]);
    const rangeSet = ref(false);
    const selectedEvents = ref([]);
    const loading = ref(null);
    const loadingEvents = ref(false);
    const buildingEvents = ref(false);
    const error = ref(null);
    const allColumns = ref([]);
    const customizeColumns = ref(false);
    const tag = ref('');
    const selectedReport = ref('');
    const reports = ref(props.template.reports);
    const createReport = ref(false);

    // Lifecycle Hooks
    const initialLoadDone = ref(false);

    onMounted(async() => {
        loadingEvents.value = true;
        if (props.eventJustCreated) {
            // New widget: event was just created by POST; only GET
            await getEvents();
        } else {
            // Widget already exists (e.g. page load): PUT then GET
            await buildEvents();
            await new Promise(resolve => setTimeout(resolve, 500));
            await getEvents();
        }
        loadingEvents.value = false;
        initialLoadDone.value = true;
    });

    watch(() => props.template, async() => {
        // Only run after initial load; then template change = user edited widget: PUT then GET
        if (!initialLoadDone.value) return;
        await buildEvents();
        await new Promise(resolve => setTimeout(resolve, 500));
        await getEvents();
    }, { deep: true });

    // Methods
    const buildEvents = async() => {
        buildingEvents.value = true;
        const body = {
            dataID: props.dataID,
            name: props.template.title.replace(/\s+/g, '_')
        }
        const response = await Events.put('', body);
        
        if (response.status !== 'success' && response.status !== 'partial_success') {
            error.value = "Failed to refresh events";
            buildingEvents.value = false;
            return;
        }
        buildingEvents.value = false;
    }

    const getEvents = async() => {
        loadingEvents.value = true;
        // Get the utc start and end dates.
        const utcStart = localToUTC(dateValue.value[0]);
        const utcEnd =  localToUTC(dateValue.value[1]);

        // The event title is connected to the event ID in the database. 
        // Format it to match the event ID.
        const eventName = props.template.title.replace(/\s+/g, '_');

        // Get the events.
        const response = await Events.get(`?dataID=${props.dataID}&name=${eventName}&startDate=${utcStart}&endDate=${utcEnd}`);

        if (response.status !== 'success') {
            error.value = "Failed to retrieve events";
            loadingEvents.value = false;
            return;
        }
        if (response.details.length === 0) {
            rawData.value = [];
            data.value = [];
            loadingEvents.value = false;
            return;
        }

        rawData.value = response.details.occurrences;

        opperations.value = response.details.comparison;
        startDate.value = response.details.startDate;

        // Grab all the keys from the first event.
        if (response.details.occurrences.length > 0) {
            allColumns.value = Object.keys(response.details.occurrences[0]);
            // Sort columns alphabetically
            allColumns.value.sort((a, b) => a.localeCompare(b));

            // Set up the event data to be displayed in the table.
            data.value = response.details.occurrences.map(event => {
                const formattedEvent = {};
                props.template.columns.forEach(column => {
                    if (column === 'startUTC') {
                        formattedEvent['Start Time'] = utcToLocal(event[column]);
                    } else if (column === 'endUTC') {
                        formattedEvent['End Time'] = utcToLocal(event[column]);
                    } else {
                        formattedEvent[column.replace(/_/g, ' ')] = event[column];
                    }
                });
                return formattedEvent;
            });
        } else {
            data.value = [];
        }
        loadingEvents.value = false;
    };

    const currentEvents = async() => {
        dateValue.value = [getPastTimestamp(24, 'local'), getPastTimestamp(0, 'local')];
        await buildEvents();
        await getEvents();
    }

    const updateTag = async (action='') => {
        loading.value = null;
        loadingEvents.value = true;

        // Grab the start times of each selected event.
        const eventUTClist = []
        selectedEvents.value.forEach(event => {
            eventUTClist.push(localToUTC(data.value[event]['Start Time']));
        });

        // Query the API to update the tag.
        const response = await Events.put('/tag', {
            dataID: props.dataID,
            name: props.template.title.replace(/\s+/g, '_'),
            eventStartUTC: eventUTClist,
            tag: action === 'remove' ? '' : tag.value
        });


        if (response.status !== 'success') {
            error.value = "Failed to update tag";
            return;
        }

        tag.value = '';

        // Update the events and get the new events.
        await buildEvents();
        await getEvents();

        loadingEvents.value = false;
    };

    const generateReport = async () => {
        loading.value = 'generateReport';
        if (selectedReport.value === 'Raw') {
            await CSVReportGenerator.generateRawReport(rawData.value, props.dataID);
        } else {
            await CSVReportGenerator.generateCustomeReport(selectedReport.value, reports.value[selectedReport.value], rawData.value, props.dataID);
        }
        loading.value = null;
    }

    const checkForCreateNew = () => {
        if (selectedReport.value === 'createNew') {
            selectedReport.value = '';
            createReport.value = true;
        }
    }

    const handleReportDetails = (eventDetails) => {
        const { name, ...details } = eventDetails;
        const newTemplate = { ...props.template };
        if (newTemplate.reports) {
            newTemplate.reports = { ...newTemplate.reports, [name]: details };
        } else {
            newTemplate.reports = { [name]: details };
        }
        reports.value = newTemplate.reports;
        emit('update', newTemplate);
    }

    const handleColumnDetails = (columns) => {
        const newTemplate = { ...props.template, columns };
        emit('update', newTemplate);
    }

    const refresh = async () => {
        await buildEvents();
        await getEvents();
    };

    defineExpose({ refresh });
</script>

<template>
    <div class="bg-mesh-surface-card rounded-md p-3">
        <!-- Header section -->
        <div class="flex flex-col sm:flex-row justify-between gap-4">
            <div class="flex-1">
                <h1 class="mt-0 mb-1 text-2xl font-bold underline">{{ template.title }}</h1>
                <p v-if="opperations"><strong>Opperation:</strong> {{ opperations }}</p>
                <p v-if="startDate"><strong>Start Date:</strong> {{ startDate }}</p>
            </div>
            <div class="flex items-center gap-4">
                <font-awesome-icon :icon="['fas', 'rotate']" class="w-6 h-6 hover:cursor-pointer" @click="currentEvents"/>
                <TrashIcon v-if="canDelete" class="w-6 h-6 hover:cursor-pointer" @click="loading = 'delete'; emit('delete')"/>
                <LoadingAnimation3 v-if="loading === 'delete'" class="ml-2" size="30px"></LoadingAnimation3>
            </div>
        </div>

        <!-- Date picker and controls section -->
        <div class="mt-4">
            <div class="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <DatePicker v-model="dateValue" class="w-full sm:w-[420px]"></DatePicker>
                <div class="flex flex-wrap gap-2">
                    <button :disabled="loading === 'setRange'" 
                            @click="getEvents" 
                            :class="{ 'bg-[#445cf2]': loading === 'setRange' }" 
                            class="default-button">
                        Set Range
                    </button>
                    <button v-if="rangeSet" 
                            @click="currentEvents" 
                            class="default-button">
                        Back To Current
                    </button>
                    <LoadingAnimation3 v-if="loading === 'setRange'" class="ml-2" size="30px"></LoadingAnimation3>
                </div>
            </div>
        </div>

        <!-- Status and controls section -->
        <div class="mt-4">
            <div v-if="!rangeSet" class="flex flex-col sm:flex-row justify-between gap-4">
                <div class="flex items-center">
                    <p v-if="!buildingEvents">Most Recent Events</p>
                    <div v-else class="flex items-center gap-2">
                        <p>Getting Events Up To Date...</p>
                        <LoadingAnimation3 size="30px"></LoadingAnimation3>
                    </div>
                </div>

                <div class="flex flex-col sm:flex-row gap-4">
                    <!-- Tag controls -->
                    <div v-if="canEdit && selectedEvents.length > 0 && loading !== 'updateTag'" class="flex flex-wrap gap-2">
                        <div class="flex-1 min-w-[200px]">
                            <label class="block">
                                Tag:
                                <input v-model="tag" class="w-full border border-gray-300 rounded-md p-2">
                            </label>
                        </div>
                        <div class="flex gap-2">
                            <button @click="updateTag" class="default-button">Add/Update Tag</button>
                            <button @click="updateTag('remove')" class="default-button">Remove Tag</button>
                        </div>
                    </div>

                    <!-- Report and column controls -->
                    <div v-else class="flex flex-col sm:flex-row gap-4">
                        <div class="flex flex-col sm:flex-row gap-2">
                            <select v-model="selectedReport" 
                                    class="default-select h-[40px] w-full sm:w-[300px]" 
                                    @change="checkForCreateNew">
                                <option disabled value="">Select a report...</option>
                                <option value="Raw">raw_data</option>
                                <option v-for="(reportData, reportKey) in reports" 
                                        :key="reportKey" 
                                        :value="reportKey">
                                    {{ reportKey }}
                                </option>
                                <option v-if="canEdit" value="createNew" class="text-blue-500">+ Create New Report</option>
                            </select>
                            <div class="flex items-center">
                                <LoadingAnimation3 v-if="loading === 'generateReport'" class="mr-2" size="30px"></LoadingAnimation3>
                                <button v-else @click="generateReport" class="default-button h-[40px]">
                                    <font-awesome-icon :icon="['fas', 'download']" class="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                        <div v-if="canEdit" class="flex items-center">
                            <button class="default-button h-[40px]" @click="customizeColumns = true">
                                Customize Columns
                            </button>
                            <LoadingAnimation3 v-if="loading === 'updateTag'" class="ml-2" size="30px"></LoadingAnimation3>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Table section -->
        <div class="mt-4">
            <GenericTable
                :data="data"
                :pagination="true"
                :selectable="canEdit"
                :loading="loadingEvents"
                @RowsSelected="selectedEvents = $event"
            />
        </div>
    </div>

    <!-- Modals -->
    <ChooseColumns v-if="customizeColumns" 
        :columns="allColumns"
        :selectedColumns="template.columns"
        @selectedColumns="handleColumnDetails"
        @close="customizeColumns = false"/>
    
    <CreateReport v-if="createReport"
        :dataID="props.dataID" 
        :availableValues="rawData[0] || {}" 
        @close="createReport = false"
        @reportDetails="handleReportDetails"
    />
</template>